import React from 'react';
import { ShieldCheck, Lock, PoundSterling, XCircle, BadgeCheck } from 'lucide-react';
import { companyInfo } from '../data/companyInfo';

// Plain link to the public Trustpilot page (the live widget returned errors and showed empty stars).
function TrustpilotLink() {
  return (
    <a
      href={companyInfo.partners?.trustpilot}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 text-sm font-semibold text-white hover:text-[#00b67a] transition-colors"
      data-testid="quote-trust-trustpilot"
    >
      Read our reviews on <span className="text-[#00b67a] font-bold">Trustpilot</span> <span aria-hidden="true">→</span>
    </a>
  );
}

/**
 * QuoteTrustBadges — compact strip of trust markers placed directly above
 * the QuoteForm on every hero. Shipping these badges next to the form
 * (not scattered further down the page) is the single biggest lift to
 * quote-form completion rate because it collapses the "should I trust
 * this company" question at the exact moment the user is deciding
 * whether to type their phone number.
 *
 * Badges shown (in order of conversion impact):
 *  1. Trustpilot LIVE widget (or static 4.9/5 fallback) — external social
 *     proof from a public reviews site
 *  2. Fixed price — kills the "hidden fees" objection
 *  3. No obligation — kills the "will I be locked in" objection
 *  4. Zero hidden fees — reinforces 2 with different wording
 *  5. GDPR / SSL / ICO — small compliance strip (calms security-cautious
 *     users, particularly older mover demographics)
 */
export default function QuoteTrustBadges({ hidePromises = false }) {

  return (
    <div className="mb-3" data-testid="quote-trust-badges">
      {/* Trustpilot row — static chip is ALWAYS visible (users always see a
          rating even if the live widget renders empty due to insufficient
          public reviews on Trustpilot's side). Live widget mounts below and
          quietly reinforces the static chip when Trustpilot has data ready,
          and also feeds machine-readable review markers to Google + AI. */}
      <div className="mb-1 flex flex-col items-center">
        <TrustpilotLink />
      </div>

      {/* Fixed-price / no-obligation / no-hidden-fees pill row — single straight line on all screens */}
      <div className="flex flex-nowrap justify-center gap-1 md:gap-1.5 mb-1.5">
        <span className="inline-flex items-center whitespace-nowrap gap-0.5 md:gap-1 text-[9px] md:text-[11px] bg-blue-50 border border-blue-300 text-blue-700 px-1.5 md:px-2 py-1 rounded-full font-bold uppercase tracking-wide" data-testid="quote-trust-fixed">
          <PoundSterling className="w-3 h-3 flex-shrink-0" /> Fixed price
        </span>
        <span className="inline-flex items-center whitespace-nowrap gap-0.5 md:gap-1 text-[9px] md:text-[11px] bg-emerald-50 border border-emerald-300 text-emerald-700 px-1.5 md:px-2 py-1 rounded-full font-bold uppercase tracking-wide" data-testid="quote-trust-no-obligation">
          <BadgeCheck className="w-3 h-3 flex-shrink-0" /> No obligation
        </span>
        {!hidePromises && (
          <span className="inline-flex items-center whitespace-nowrap gap-0.5 md:gap-1 text-[9px] md:text-[11px] bg-emerald-50 border border-emerald-300 text-emerald-700 px-1.5 md:px-2 py-1 rounded-full font-bold uppercase tracking-wide" data-testid="quote-trust-no-fees">
            <XCircle className="w-3 h-3 flex-shrink-0" /> Zero hidden fees
          </span>
        )}
      </div>

      {/* Compliance / security micro-strip — single straight line on all screens */}
      <div className="flex flex-nowrap justify-center items-center gap-1.5 md:gap-2 text-[9px] md:text-[11px] text-blue-100 font-medium border-t border-white/15 pt-2" data-testid="quote-trust-compliance">
        <span className="inline-flex items-center whitespace-nowrap gap-1"><ShieldCheck className="w-3 h-3 flex-shrink-0 text-blue-300" /> GDPR compliant</span>
        <span className="text-white/30">·</span>
        <span className="inline-flex items-center whitespace-nowrap gap-1"><Lock className="w-3 h-3 flex-shrink-0 text-blue-300" /> SSL secured</span>
        <span className="text-white/30">·</span>
        <span className="inline-flex items-center whitespace-nowrap gap-1"><BadgeCheck className="w-3 h-3 flex-shrink-0 text-blue-300" /> ICO registered</span>
      </div>
    </div>
  );
}
